/**
 * SP5 Wave 3 — CISO `/benchmarks` KPI rail.
 *
 * Renders four cards above the existing benchmarks list:
 *   Reports · Publishers · Topics · Last added
 *
 * All values come from `GET /api/benchmarks/stats` which applies the
 * `status='published' AND suppressed_at IS NULL` filter server-side. Each
 * state (loading, success, empty, error) renders in-place so the rail never
 * collapses the layout.
 */

import { KpiMetricCard } from '@/components/shared/KpiMetricCard'
import {
  useBenchmarksStats,
  type BenchmarksStatsFilters,
} from '@/hooks/useBenchmarksStats'

function formatRelative(iso: string | null): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const days = Math.max(0, Math.floor((Date.now() - then) / 86_400_000))
  if (days === 0) return 'today'
  if (days === 1) return '1d ago'
  return `${days}d ago`
}

/**
 * Wave-1 A1e — accepts the same filter dimensions as the list query so
 * the KPI rail and the visible catalogue agree under any user-applied
 * filter. Both endpoints apply `status='published' AND suppressed_at IS NULL`
 * server-side (alignment guarantee).
 */
export interface BenchmarksKpiRailProps {
  filters?: BenchmarksStatsFilters
}

export function BenchmarksKpiRail({ filters = {} }: BenchmarksKpiRailProps = {}) {
  const { data, isLoading, isError } = useBenchmarksStats(filters)

  if (isError) {
    return (
      <div
        data-testid="kpi-rail"
        role="status"
        aria-live="polite"
        className="mb-6 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-200"
      >
        KPI rail temporarily unavailable.
      </div>
    )
  }

  if (isLoading || !data) {
    return (
      <div
        data-testid="kpi-rail"
        className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4"
        aria-busy="true"
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            data-testid="kpi-card-skeleton"
            className="h-24 animate-pulse rounded-md bg-muted"
          />
        ))}
      </div>
    )
  }

  return (
    <div
      data-testid="kpi-rail"
      className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4"
    >
      <KpiMetricCard label="Reports" value={String(data.reportCount)} />
      <KpiMetricCard label="Publishers" value={String(data.publisherCount)} />
      <KpiMetricCard label="Topics" value={String(data.topicCount)} />
      <KpiMetricCard
        label="Last added"
        value={formatRelative(data.lastAddedAt)}
      />
    </div>
  )
}
